import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Container, Row, Col, Card, Table, Button, Form, InputGroup, Badge, Pagination, Spinner, Alert } from 'react-bootstrap';
import { FaDownload, FaSearch, FaFilter, FaUsers, FaCalendar, FaPhone, FaEnvelope, FaDatabase, FaCloud, FaCopy } from 'react-icons/fa';
import { getMateriasSeleccionadasTexto } from '../data/materiasPendientes';

const Registros = () => {
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartamento, setFilterDepartamento] = useState('');
  const [filterTipoInstitucion, setFilterTipoInstitucion] = useState('');
  const [filterModalidad, setFilterModalidad] = useState('');
  const [filterFechaDesde, setFilterFechaDesde] = useState('');
  const [filterFechaHasta, setFilterFechaHasta] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(10);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [stats, setStats] = useState({ mongoCount: null, zohoCount: null, zohoError: null });
  const [statsLoading, setStatsLoading] = useState(true);
  const [dailyDate, setDailyDate] = useState(new Date().toISOString().slice(0, 10));
  const [dailyStats, setDailyStats] = useState({ mongoCount: null, zohoCount: null, zohoError: null });
  const [dailyLoading, setDailyLoading] = useState(false);

  const fetchDailyStats = (date) => {
    setDailyLoading(true);
    axios.get(`${process.env.REACT_APP_API_URL}/form/stats/daily?date=${date}`)
      .then(response => {
        setDailyStats(response.data);
        setDailyLoading(false);
      })
      .catch(error => {
        console.error('Error al cargar estadísticas diarias:', error);
        setDailyLoading(false);
      });
  };

  useEffect(() => {
    const password = prompt('Ingrese la contraseña de administrador:');
    if (password !== 'fudacion3f123!') {
      alert('Contraseña incorrecta. No tienes permiso para acceder.');
      return;
    }
    setAuthorized(true);

    axios.get(`${process.env.REACT_APP_API_URL}/form`)
      .then(response => {
        setRegistros(response.data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error al cargar los registros:', error);
        setLoading(false);
      });

    axios.get(`${process.env.REACT_APP_API_URL}/form/stats`)
      .then(response => {
        setStats(response.data);
        setStatsLoading(false);
      })
      .catch(error => {
        console.error('Error al cargar estadísticas:', error);
        setStatsLoading(false);
      });
  }, []);

  // Filtros y búsqueda
  const filteredRegistros = useMemo(() => {
    return registros.filter(registro => {
      const matchesSearch = 
        registro.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        registro.apellido?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        registro.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        registro.telefono?.includes(searchTerm) ||
        registro.cedula?.includes(searchTerm) ||
        registro.barrio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        registro.entryUrl?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getMateriasSeleccionadasTexto(registro.materiasPendientesSeleccionadas)
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      
      const matchesDepartamento = !filterDepartamento || registro.departamento === filterDepartamento;
      const matchesTipoInstitucion = !filterTipoInstitucion || registro.tipoInstitucion === filterTipoInstitucion;
      const matchesModalidad = !filterModalidad || registro.modalidad === filterModalidad;
      
      let matchesFechaDesde = true;
      let matchesFechaHasta = true;
      if (filterFechaDesde && registro.createdAt) {
        const fechaRegistro = new Date(registro.createdAt);
        const [year, month, day] = filterFechaDesde.split('-').map(Number);
        const fechaDesde = new Date(year, month - 1, day, 0, 0, 0, 0);
        matchesFechaDesde = fechaRegistro >= fechaDesde;
      }
      if (filterFechaHasta && registro.createdAt) {
        const fechaRegistro = new Date(registro.createdAt);
        const [year, month, day] = filterFechaHasta.split('-').map(Number);
        const fechaHasta = new Date(year, month - 1, day, 23, 59, 59, 999);
        matchesFechaHasta = fechaRegistro <= fechaHasta;
      }
      
      return matchesSearch && matchesDepartamento && matchesTipoInstitucion && matchesModalidad && matchesFechaDesde && matchesFechaHasta;
    });
  }, [registros, searchTerm, filterDepartamento, filterTipoInstitucion, filterModalidad, filterFechaDesde, filterFechaHasta]);

  // Ordenamiento
  const sortedRegistros = useMemo(() => {
    return [...filteredRegistros].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      if (sortField === 'createdAt' || sortField === 'nacimiento') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredRegistros, sortField, sortDirection]);

  // Paginación
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = sortedRegistros.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(sortedRegistros.length / recordsPerPage);

  // Obtener valores únicos para filtros
  const departamentos = [...new Set(registros.map(r => r.departamento).filter(Boolean))];
  const tiposInstitucion = [...new Set(registros.map(r => r.tipoInstitucion).filter(Boolean))];
  const modalidades = [...new Set(registros.map(r => r.modalidad).filter(Boolean))];

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterDepartamento('');
    setFilterTipoInstitucion('');
    setFilterModalidad('');
    setFilterFechaDesde('');
    setFilterFechaHasta('');
    setCurrentPage(1);
  };

  const downloadCSV = () => {
    const headers = [
      'Nombre','Apellido','Nacimiento','Teléfono','Otro Telefono','Email','URL de entrada','Cédula','Departamento',
      'Barrio','Tipo Centro','Tipo Institución','Bachillerato (UTU)','Orientación 5to','Orientación 6to',
      'Pendientes 4to','Pendientes 5to',
      'Pendientes 6to','Total pendientes','Materias seleccionadas','Modalidad','Horario','Creado'
    ];

    const rows = filteredRegistros.map(r => [
      r.nombre, r.apellido,
      new Date(r.nacimiento).toLocaleDateString('es-UY'),
      r.telefono, r.otroTelefono, r.email, r.entryUrl, r.cedula, r.departamento, r.barrio,
      r.tipoCentro, r.tipoInstitucion, r.bachillerato, r.orientacion5to, r.orientacion6to,
      r.pendientes4to, r.pendientes5to, r.pendientes6to,
      typeof r.totalMaterias === 'number' ? r.totalMaterias : '',
      getMateriasSeleccionadasTexto(r.materiasPendientesSeleccionadas),
      r.modalidad, r.horario,
      new Date(r.createdAt).toLocaleString('es-UY')
    ]);

    const sep = ';';
    const quote = v => `"${String(v ?? '').replace(/"/g, '""')}"`;

    const csvLines = [
      headers.map(quote).join(sep),
      ...rows.map(row => row.map(quote).join(sep))
    ];

    const csvContent = '\uFEFF' + csvLines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `registros_fundacion3f_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  if (!authorized) {
    return (
      <Container className="mt-5">
        <Alert variant="danger" className="text-center">
          <h4>Acceso Denegado</h4>
          <p>No tienes permisos para acceder a esta página.</p>
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" style={{ color: '#ffffff' }} />
        <p className="mt-3">Cargando registros...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* CSS Global para sobrescribir Bootstrap */}
      <style>
        {`
          .table thead th {
            background-color: #000000 !important;
            color: #ffffff !important;
            border-color: #ffffff !important;
          }
          .badge {
            background-color: #ffffff !important;
            color: #000000 !important;
            border: 1px solid #000000 !important;
          }
          .spinner-border {
            color: #ffffff !important;
            border-color: #ffffff!important;
          }
          .table-hover tbody tr:hover {
            background-color: rgba(174, 255, 4, 0.1) !important;
          }
        `}
      </style>
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm">
            <Card.Body>
              <Row className="align-items-center">
                <Col md={6}>
                  <h2 className="mb-0">
                    <FaUsers className="me-2" />
                    Panel de Registros - Fundación 3F
                  </h2>
                  <p className="text-muted mb-0">Gestión de inscripciones y datos de estudiantes</p>
                </Col>
                <Col md={6} className="text-end">
                  <Badge 
                    className="me-2"
                    style={{
                      backgroundColor: '#ffffff',
                      color: '#000000',
                      border: '1px solid #000000'
                    }}
                  >
                    Total: {registros.length} registros
                  </Badge>
                  <Badge 
                    style={{
                      backgroundColor: '#000000',
                      color: '#ffffff',
                      border: '1px solid #ffffff'
                    }}
                  >
                    Filtrados: {filteredRegistros.length}
                  </Badge>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Conteo desde el 19 de enero */}
      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm" style={{ border: '2px solid #000000' }}>
            <Card.Header style={{ backgroundColor: '#000000', color: '#ffffff' }}>
              <h5 className="mb-0">
                <FaCalendar className="me-2" />
                Registros desde el 19 de Enero de 2026
              </h5>
            </Card.Header>
            <Card.Body>
              {statsLoading ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" style={{ color: '#000000' }} />
                  <span className="ms-2">Cargando estadísticas...</span>
                </div>
              ) : (
                <Row className="text-center">
                  <Col md={6} className="mb-3 mb-md-0">
                    <Card style={{ border: '1px solid #000000' }}>
                      <Card.Body>
                        <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
                          <FaDatabase className="me-1" /> MongoDB
                        </div>
                        <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#000000' }}>
                          {stats.mongoCount !== null ? stats.mongoCount : '—'}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#888' }}>registros</div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card style={{ border: '1px solid #000000' }}>
                      <Card.Body>
                        <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
                          <FaCloud className="me-1" /> Zoho CRM
                        </div>
                        {stats.zohoError ? (
                          <>
                            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#cc0000' }}>Error</div>
                            <div style={{ fontSize: '0.8rem', color: '#cc0000' }}>{stats.zohoError}</div>
                          </>
                        ) : (
                          <>
                            <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#000000' }}>
                              {stats.zohoCount !== null ? stats.zohoCount : '—'}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#888' }}>contactos</div>
                          </>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Conteo por día */}
      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm" style={{ border: '2px solid #000000' }}>
            <Card.Header style={{ backgroundColor: '#000000', color: '#ffffff' }}>
              <Row className="align-items-center">
                <Col md={6}>
                  <h5 className="mb-0">
                    <FaCalendar className="me-2" />
                    Registros por Día
                  </h5>
                </Col>
                <Col md={6}>
                  <div className="d-flex align-items-center justify-content-end gap-2">
                    <Form.Control
                      type="date"
                      value={dailyDate}
                      onChange={(e) => setDailyDate(e.target.value)}
                      style={{ maxWidth: '200px' }}
                    />
                    <Button
                      onClick={() => fetchDailyStats(dailyDate)}
                      disabled={dailyLoading}
                      style={{
                        backgroundColor: '#ffffff',
                        borderColor: '#ffffff',
                        color: '#000000',
                        fontWeight: 'bold'
                      }}
                    >
                      {dailyLoading ? <Spinner animation="border" size="sm" /> : 'Consultar'}
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body>
              {dailyStats.mongoCount === null && !dailyLoading ? (
                <div className="text-center py-3 text-muted">
                  Seleccioná una fecha y presioná "Consultar" para ver los registros del día
                </div>
              ) : dailyLoading ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" style={{ color: '#000000' }} />
                  <span className="ms-2">Consultando...</span>
                </div>
              ) : (
                <Row className="text-center">
                  <Col md={6} className="mb-3 mb-md-0">
                    <Card style={{ border: '1px solid #000000' }}>
                      <Card.Body>
                        <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
                          <FaDatabase className="me-1" /> MongoDB
                        </div>
                        <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#000000' }}>
                          {dailyStats.mongoCount !== null ? dailyStats.mongoCount : '—'}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#888' }}>registros</div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card style={{ border: '1px solid #000000' }}>
                      <Card.Body>
                        <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
                          <FaCloud className="me-1" /> Zoho CRM
                        </div>
                        {dailyStats.zohoError ? (
                          <>
                            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#cc0000' }}>Error</div>
                            <div style={{ fontSize: '0.8rem', color: '#cc0000' }}>{dailyStats.zohoError}</div>
                          </>
                        ) : (
                          <>
                            <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#000000' }}>
                              {dailyStats.zohoCount !== null ? dailyStats.zohoCount : '—'}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#888' }}>contactos</div>
                          </>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filtros y Búsqueda */}
      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm">
            <Card.Header className="bg-light">
              <h5 className="mb-0">
                <FaFilter className="me-2" />
                Filtros y Búsqueda
              </h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={4} className="mb-3">
                  <Form.Label>Búsqueda General</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaSearch /></InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Buscar por nombre, email, teléfono..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label>Departamento</Form.Label>
                  <Form.Select
                    value={filterDepartamento}
                    onChange={(e) => setFilterDepartamento(e.target.value)}
                  >
                    <option value="">Todos</option>
                    {departamentos.map(dep => (
                      <option key={dep} value={dep}>{dep}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label>Tipo Institución</Form.Label>
                  <Form.Select
                    value={filterTipoInstitucion}
                    onChange={(e) => setFilterTipoInstitucion(e.target.value)}
                  >
                    <option value="">Todos</option>
                    {tiposInstitucion.map(tipo => (
                      <option key={tipo} value={tipo}>{tipo}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={2} className="mb-3">
                  <Form.Label>Modalidad</Form.Label>
                  <Form.Select
                    value={filterModalidad}
                    onChange={(e) => setFilterModalidad(e.target.value)}
                  >
                    <option value="">Todas</option>
                    {modalidades.map(mod => (
                      <option key={mod} value={mod}>{mod}</option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>
              <Row>
                <Col md={3} className="mb-3">
                  <Form.Label><FaCalendar className="me-1" />Fecha Inscripción Desde</Form.Label>
                  <Form.Control
                    type="date"
                    value={filterFechaDesde}
                    onChange={(e) => setFilterFechaDesde(e.target.value)}
                  />
                </Col>
                <Col md={3} className="mb-3">
                  <Form.Label><FaCalendar className="me-1" />Fecha Inscripción Hasta</Form.Label>
                  <Form.Control
                    type="date"
                    value={filterFechaHasta}
                    onChange={(e) => setFilterFechaHasta(e.target.value)}
                  />
                </Col>
                <Col md={4}></Col>
                <Col md={2} className="mb-3 d-flex align-items-end">
                  <div className="w-100">
                    <Button 
                      onClick={clearFilters} 
                      className="w-100"
                      style={{
                        backgroundColor: '#ffffff',
                        borderColor: '#000000',
                        color: '#000000',
                        border: '1px solid #000000'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#000000';
                        e.target.style.color = '#ffffff';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#ffffff';
                        e.target.style.color = '#000000';
                      }}
                    >
                      Limpiar Filtros
                    </Button>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col className="text-end">
                  <Button 
                    onClick={downloadCSV}
                    style={{
                      backgroundColor: '#ffffff',
                      borderColor: '#000000',
                      color: '#000000',
                      border: '1px solid #000000'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#000000';
                      e.target.style.color = '#ffffff';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#ffffff';
                      e.target.style.color = '#000000';
                    }}
                  >
                    <FaDownload className="me-2" />
                    Descargar CSV ({filteredRegistros.length} registros)
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabla de Registros */}
      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead style={{ backgroundColor: '#000000 !important', color: '#ffffff !important' }}>
                    <tr>
                      <th 
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleSort('nombre')}
                      >
                        Nombre {sortField === 'nombre' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th 
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleSort('apellido')}
                      >
                        Apellido {sortField === 'apellido' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th 
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleSort('nacimiento')}
                      >
                        <FaCalendar className="me-1" />
                        Nacimiento {sortField === 'nacimiento' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th><FaPhone className="me-1" />Teléfono</th>
                      <th><FaPhone className="me-1" />Otro Tel.</th>
                      <th><FaEnvelope className="me-1" />Email</th>
                      <th>URL de entrada</th>
                      <th>Cédula</th>
                      <th>Departamento</th>
                      <th>Barrio</th>
                      <th>Tipo Centro</th>
                      <th>Tipo Institución</th>
                      <th>Bachillerato</th>
                      <th>Pend. 4to</th>
                      <th>Pend. 5to</th>
                      <th>Pend. 6to</th>
                      <th>Total pend.</th>
                      <th>Materias</th>
                      <th>Modalidad</th>
                      <th>Horario</th>
                      <th 
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleSort('createdAt')}
                      >
                        Creado {sortField === 'createdAt' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRecords.length === 0 ? (
                      <tr>
                        <td colSpan="21" className="text-center py-4 text-muted">
                          No se encontraron registros con los filtros aplicados
                        </td>
                      </tr>
                    ) : (
                      currentRecords.map((registro, index) => (
                        <tr key={registro._id} className={index % 2 === 0 ? 'bg-light' : ''}>
                          <td className="fw-bold">{registro.nombre}</td>
                          <td>{registro.apellido}</td>
                          <td>{new Date(registro.nacimiento).toLocaleDateString('es-UY')}</td>
                          <td>
                            <Badge 
                              style={{
                                backgroundColor: '#ffffff',
                                color: '#000000',
                                border: '1px solid #000000'
                              }}
                            >
                              {registro.telefono}
                            </Badge>
                          </td>
                          <td>
                            {registro.otroTelefono ? (
                              <Badge 
                                style={{
                                  backgroundColor: '#ffffff',
                                  color: '#000000',
                                  border: '1px solid #000000'
                                }}
                              >
                                {registro.otroTelefono}
                              </Badge>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>
                            <small>{registro.email}</small>
                          </td>
                          <td style={{ minWidth: '120px', maxWidth: '150px' }}>
                            {registro.entryUrl ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <div 
                                  style={{ 
                                    maxHeight: '40px', 
                                    overflow: 'auto', 
                                    fontSize: '11px',
                                    flex: 1,
                                    wordBreak: 'break-all'
                                  }}
                                >
                                  {registro.entryUrl}
                                </div>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  style={{ padding: '2px 6px', fontSize: '10px' }}
                                  onClick={() => {
                                    navigator.clipboard.writeText(registro.entryUrl);
                                  }}
                                  title="Copiar URL"
                                >
                                  <FaCopy />
                                </Button>
                              </div>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>{registro.cedula || '-'}</td>
                          <td>
                            <Badge 
                              style={{
                                backgroundColor: '#ffffff',
                                color: '#000000',
                                border: '1px solid #000000'
                              }}
                            >
                              {registro.departamento}
                            </Badge>
                          </td>
                          <td>{registro.barrio || '-'}</td>
                          <td>{registro.tipoCentro}</td>
                          <td>
                            <Badge 
                              style={{
                                backgroundColor: '#ffffff',
                                color: '#000000',
                                border: '1px solid #000000'
                              }}
                            >
                              {registro.tipoInstitucion}
                            </Badge>
                          </td>
                          <td>{registro.bachillerato || '-'}</td>
                          <td className="text-center">
                            <Badge 
                              style={{
                                backgroundColor: registro.pendientes4to === '0' ? '#ffffff' : '#000000',
                                color: registro.pendientes4to === '0' ? '#000000' : '#ffffff',
                                border: '1px solid #000000'
                              }}
                            >
                              {registro.pendientes4to}
                            </Badge>
                          </td>
                          <td className="text-center">
                            <Badge 
                              style={{
                                backgroundColor: registro.pendientes5to === '0' ? '#ffffff' : '#000000',
                                color: registro.pendientes5to === '0' ? '#000000' : '#ffffff',
                                border: '1px solid #000000'
                              }}
                            >
                              {registro.pendientes5to}
                            </Badge>
                          </td>
                          <td className="text-center">
                            <Badge 
                              style={{
                                backgroundColor: registro.pendientes6to === '0' ? '#ffffff' : '#000000',
                                color: registro.pendientes6to === '0' ? '#000000' : '#ffffff',
                                border: '1px solid #000000'
                              }}
                            >
                              {registro.pendientes6to}
                            </Badge>
                          </td>
                          <td className="text-center">
                            <Badge
                              style={{
                                backgroundColor: '#ffffff',
                                color: '#000000',
                                border: '1px solid #000000'
                              }}
                            >
                              {typeof registro.totalMaterias === 'number' ? registro.totalMaterias : '-'}
                            </Badge>
                          </td>
                          <td style={{ minWidth: '260px' }}>
                            <small>{getMateriasSeleccionadasTexto(registro.materiasPendientesSeleccionadas)}</small>
                          </td>
                          <td>
                            <Badge 
                              style={{
                                backgroundColor: '#ffffff',
                                color: '#000000',
                                border: '1px solid #000000'
                              }}
                            >
                              {registro.modalidad}
                            </Badge>
                          </td>
                          <td>{registro.horario}</td>
                          <td>
                            <small className="text-muted">
                              {new Date(registro.createdAt).toLocaleDateString('es-UY')}
                            </small>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Paginación */}
      {totalPages > 1 && (
        <Row className="mt-4">
          <Col className="d-flex justify-content-center">
            <style>
              {`
                .custom-pagination .page-link {
                  background-color: #ffffff !important;
                  border-color: #000000 !important;
                  color: #000000 !important;
                  border-width: 1px !important;
                }
                .custom-pagination .page-link:hover {
                  background-color: #000000 !important;
                  border-color: #000000 !important;
                  color: #ffffff !important;
                }
                .custom-pagination .page-item.active .page-link {
                  background-color: #000000 !important;
                  border-color: #000000 !important;
                  color: #ffffff !important;
                }
                .custom-pagination .page-item.disabled .page-link {
                  background-color: #cccccc !important;
                  border-color: #999999 !important;
                  color: #666666 !important;
                }
              `}
            </style>
            <Pagination className="custom-pagination">
              <Pagination.First 
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev 
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
              />
              
              {[...Array(totalPages)].map((_, index) => {
                const page = index + 1;
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 2 && page <= currentPage + 2)
                ) {
                  return (
                    <Pagination.Item
                      key={page}
                      active={page === currentPage}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </Pagination.Item>
                  );
                } else if (
                  page === currentPage - 3 ||
                  page === currentPage + 3
                ) {
                  return <Pagination.Ellipsis key={page} />;
                }
                return null;
              })}
              
              <Pagination.Next 
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last 
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </Col>
        </Row>
      )}

      {/* Footer con información */}
      <Row className="mt-4">
        <Col>
          <Card className="bg-light">
            <Card.Body className="text-center">
              <small className="text-muted">
                Mostrando {indexOfFirstRecord + 1} - {Math.min(indexOfLastRecord, filteredRegistros.length)} de {filteredRegistros.length} registros
                {filteredRegistros.length !== registros.length && ` (filtrados de ${registros.length} total)`}
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Registros;
